import type { MissionView, PreflightRow } from '../api/missions';
import { ca } from '../i18n/ca';

const d = ca.design;

function words(r: PreflightRow) {
  return [
    d.preflightKinds[r.kind] ?? r.kind,
    r.label,
    d.preflightChecks[r.check] ?? r.check,
  ] as const;
}

/**
 * What the platform corrected before play (release D, D11.9), for the
 * mission's editors: the last correction stored with its revision, and what
 * the next session will move, will refuse or only says. Nothing for anybody
 * else (it names enemy positions), and nothing when there is nothing to say.
 */
export function PreflightCard({ mission }: { mission: MissionView }) {
  if (!mission.canEdit) return null;
  const done = mission.definition.preflight;
  const next = mission.preflight;
  const pending = next ? [...next.unplaceable, ...next.moved, ...next.left] : [];
  if (!done && (!next || (next.surveyed && pending.length === 0))) return null;
  return (
    <section className="card" aria-label={d.preflightCard}>
      <h2>{d.preflightCard}</h2>
      <p className="muted small">{d.preflightCardHint}</p>
      {done && done.moved.length > 0 && (
        <>
          <p className="small">{d.preflightDone(done.revision)}</p>
          <ul className="plain small">
            {done.moved.map((r) => (
              <li key={`done-${r.kind}-${r.id}`}>{d.preflightDoneRow(...words(r), r.metres)}</li>
            ))}
          </ul>
        </>
      )}
      {next && !next.surveyed && <p className="muted small">{d.preflightUnsurveyed}</p>}
      {next && next.unplaceable.length > 0 && (
        <>
          <p className="small error">{d.preflightStuck}</p>
          <ul className="plain small">
            {next.unplaceable.map((r) => (
              <li key={`stuck-${r.kind}-${r.id}`}>{d.preflightLeft(...words(r))}</li>
            ))}
          </ul>
        </>
      )}
      {next && next.moved.length > 0 && (
        <>
          <p className="small">{d.preflightNext}</p>
          <ul className="plain small">
            {next.moved.map((r) => (
              <li key={`next-${r.kind}-${r.id}`}>{d.preflightDoneRow(...words(r), r.metres)}</li>
            ))}
          </ul>
        </>
      )}
      {next && next.left.length > 0 && (
        <>
          <p className="small muted">{d.preflightSaid}</p>
          <ul className="plain small muted">
            {next.left.map((r) => (
              <li key={`left-${r.kind}-${r.id}`}>{d.preflightLeft(...words(r))}</li>
            ))}
          </ul>
        </>
      )}
      {next && next.surveyed && pending.length === 0 && (
        <p className="muted small">{d.preflightNothing}</p>
      )}
    </section>
  );
}
